import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Turn {
  role: 'assistant' | 'user';
  content: string;
  attachId?: string;
  deleted?: boolean;
}

type Phase = 'chat' | 'submitting' | 'filed' | 'done' | 'error';
type ModeId = 'feedback' | 'chat' | 'bug';

interface Mode {
  id: ModeId;
  label: string;
  comingSoon: boolean;
}

interface Preview {
  plain_summary: string;
  github_url?: string;
}

interface Attachment {
  id: string;
  type: 'screenshot' | 'file';
  url: string;
  dataUrl?: string;
  name: string;
  screenshotKind?: 'page' | 'panel';
}

interface NavEntry {
  url: string;
  title: string;
  ts: string;
}

interface SavedSession {
  id: string;
  turns: Turn[];
  attachments: Attachment[];
  phase: Phase;
  preview: Preview | null;
  statusMsg: string;
  submissionId: string | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MODES: Mode[] = [
  { id: 'feedback', label: 'Feedback',   comingSoon: false },
  { id: 'chat',     label: 'Chat',       comingSoon: true  },
  { id: 'bug',      label: 'Bug Report', comingSoon: true  },
];

const MODE_QUESTIONS: Record<ModeId, string[]> = {
  feedback: [
    "Hi! I can help you file a GitHub issue on this portal. What's going wrong, or what would you like to see improved?",
    "Got it. Where does this happen — which page or feature? And how often does it occur?",
    "Thanks. What would you expect to happen instead, or what outcome are you hoping for?",
  ],
  chat: [],
  bug: [],
};

// Env-only: rotating the panel API key in the backend would silently
// keep returning 401 if a fallback were baked in here.
const API_URL = process.env.REACT_APP_PANEL_API_URL ?? '';
const API_KEY = process.env.REACT_APP_PANEL_API_KEY ?? '';
const REPO = 'Consiliency/consiliency-portal';
const PANEL_REPO = 'Consiliency/consiliency-panel';

// ── Helpers ───────────────────────────────────────────────────────────────────

function apiHeaders(githubLogin: string): Record<string, string> {
  const h: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${API_KEY}`,
  };
  if (githubLogin) h['x-github-login'] = githubLogin;
  return h;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function urlToComponentHint(path: string): string {
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return 'apps/backstage/packages/app/src/App.tsx';
  const mapped = segments.map(s =>
    /^[0-9a-f-]{8,}$|^\d+$/.test(s) ? '[id]' : s
  );
  return `apps/backstage/packages/app/src/${mapped.join('/')}.tsx`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PanelWidget() {
  const identityApi = useApi(identityApiRef);
  const [githubLogin, setGithubLogin] = useState('');

  useEffect(() => {
    if (!API_URL || !API_KEY) {
      // eslint-disable-next-line no-console
      console.warn(
        '[PanelWidget] REACT_APP_PANEL_API_URL or REACT_APP_PANEL_API_KEY missing — widget disabled.',
      );
    }
  }, []);

  // ── Core panel state ────────────────────────────────────────────────────────
  const [open, setOpen]               = useState(false);
  const [activeMode, setActiveMode]   = useState<ModeId>('feedback');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [turns, setTurns]             = useState<Turn[]>([]);
  const [input, setInput]             = useState('');
  const [phase, setPhase]             = useState<Phase>('chat');
  const [preview, setPreview]         = useState<Preview | null>(null);
  const [statusMsg, setStatusMsg]     = useState('');
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const currentSessionId = useRef(Math.random().toString(36).slice(2));
  const questionIdx = useRef(0);

  // ── Capture state ───────────────────────────────────────────────────────────
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [breadcrumb, setBreadcrumb]   = useState<NavEntry[]>([]);
  const consoleErrorsRef              = useRef<string[]>([]);
  const consoleWarningsRef            = useRef<string[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isDragOver, setIsDragOver]   = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const mediaRecorderRef              = useRef<MediaRecorder | null>(null);
  const audioChunksRef                = useRef<Blob[]>([]);
  const speechRecRef                  = useRef<{ stop: () => void } | null>(null);
  const fileInputRef                  = useRef<HTMLInputElement>(null);

  const bottomRef       = useRef<HTMLDivElement>(null);
  const dropdownRef     = useRef<HTMLDivElement>(null);
  const panelRef        = useRef<HTMLDivElement>(null);
  const hasCapturedRef  = useRef(false);

  // ── Auth ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => {
      const ref = identity.userEntityRef;
      const login = ref.split('/').pop() ?? '';
      setGithubLogin(login);
    }).catch(() => {});
  }, [identityApi]);

  // ── Console buffering (mount once) ──────────────────────────────────────────
  useEffect(() => {
    const origError = console.error.bind(console);
    const origWarn  = console.warn.bind(console);
    console.error = (...args: unknown[]) => {
      consoleErrorsRef.current.push(args.map(String).join(' '));
      origError(...args);
    };
    console.warn = (...args: unknown[]) => {
      if (consoleWarningsRef.current.length < 30) {
        consoleWarningsRef.current.push(args.map(String).join(' '));
      }
      origWarn(...args);
    };
    return () => { console.error = origError; console.warn = origWarn; };
  }, []);

  // ── Navigation breadcrumb (popstate) ────────────────────────────────────────
  useEffect(() => {
    const addEntry = () => {
      if (!open) return;
      const entry: NavEntry = {
        url: window.location.href,
        title: document.title,
        ts: new Date().toISOString(),
      };
      setBreadcrumb(prev => {
        if (prev.at(-1)?.url === entry.url) return prev;
        return [...prev, entry];
      });
    };
    window.addEventListener('popstate', addEntry);
    return () => window.removeEventListener('popstate', addEntry);
  }, [open]);

  // ── Auto-capture on open ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || hasCapturedRef.current) return;
    hasCapturedRef.current = true;
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    handleScreenshot();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Guided questions ─────────────────────────────────────────────────────────
  const guidedQuestions = MODE_QUESTIONS[activeMode];

  useEffect(() => {
    if (open && turns.length === 0 && guidedQuestions.length > 0) {
      setTurns([{ role: 'assistant', content: guidedQuestions[0] }]);
      questionIdx.current = 0;
    }
  }, [open, turns.length, guidedQuestions]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, statusMsg]);

  // ── Close dropdown on outside click ─────────────────────────────────────────
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    setTurns([]); setInput(''); setPhase('chat'); setPreview(null); setStatusMsg('');
    setAttachments([]); setBreadcrumb([]);
    setSubmissionId(null);
    questionIdx.current = 0;
    hasCapturedRef.current = false;
  }, []);

  const handleNewSession = useCallback(() => {
    setSavedSessions(prev => [...prev, {
      id: currentSessionId.current,
      turns, attachments, phase, preview, statusMsg, submissionId,
    }]);
    currentSessionId.current = Math.random().toString(36).slice(2);
    reset();
  }, [turns, attachments, phase, preview, statusMsg, submissionId, reset]);

  const handleSwitchSession = useCallback((session: SavedSession) => {
    setSavedSessions(prev => [
      ...prev.filter(s => s.id !== session.id),
      { id: currentSessionId.current, turns, attachments, phase, preview, statusMsg, submissionId },
    ]);
    currentSessionId.current = session.id;
    setTurns(session.turns);
    setAttachments(session.attachments);
    setPhase(session.phase);
    setPreview(session.preview);
    setStatusMsg(session.statusMsg);
    setSubmissionId(session.submissionId);
    hasCapturedRef.current = true;
  }, [turns, attachments, phase, preview, statusMsg, submissionId]);

  const selectMode = useCallback((mode: Mode) => {
    if (mode.comingSoon) return;
    setActiveMode(mode.id);
    setDropdownOpen(false);
    reset();
    setOpen(true);
  }, [reset]);

  // ── File upload ──────────────────────────────────────────────────────────────

  const uploadFile = useCallback(async (file: File) => {
    let blob: Blob = file;
    let dataUrl: string | undefined;
    if (file.type.startsWith('image/')) {
      dataUrl = await blobToDataUrl(file);
    }
    try {
      const headers = apiHeaders(githubLogin);
      const urlRes = await fetch(`${API_URL}/v1/panel/upload-url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ filename: file.name, contentType: file.type }),
      });
      if (!urlRes.ok) return;
      const { uploadUrl, storageUrl } = await urlRes.json() as { uploadUrl: string; storageUrl: string };
      await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': file.type } });
      setAttachments(prev => [...prev, {
        id: Math.random().toString(36).slice(2),
        type: file.type.startsWith('image/') ? 'screenshot' : 'file',
        url: storageUrl,
        dataUrl,
        name: file.name,
      }]);
    } catch { /* ignore */ }
  }, [githubLogin]);

  // ── Screenshot ───────────────────────────────────────────────────────────────

  const handleScreenshot = useCallback(async () => {
    setIsCapturing(true);
    const ts = Date.now();
    try {
      const mod = await import('modern-screenshot' as any);
      const [pageResult, panelResult] = await Promise.allSettled([
        mod.domToBlob(document.documentElement, {
          quality: 0.9, type: 'image/png',
          fetch: { bypassingCache: true }, timeout: 10000,
          filter: (node: Node) => node !== panelRef.current,
        }),
        panelRef.current
          ? mod.domToBlob(panelRef.current, { quality: 0.9, type: 'image/png', timeout: 10000 })
          : Promise.reject('panel not mounted'),
      ]);

      const captures: Array<{ blob: Blob; kind: 'page' | 'panel'; label: string }> = [];
      if (pageResult.status === 'fulfilled') captures.push({ blob: pageResult.value as Blob, kind: 'page', label: '📷 Page screenshot captured.' });
      if (panelResult.status === 'fulfilled') captures.push({ blob: panelResult.value as Blob, kind: 'panel', label: '🖼️ Panel screenshot captured.' });

      if (captures.length === 0) throw new Error('both captures failed');

      for (const { blob, kind, label } of captures) {
        const attachId = Math.random().toString(36).slice(2);
        const filename = `screenshot-${kind}-${ts}.png`;
        const dataUrl = await blobToDataUrl(blob);
        setAttachments(prev => [...prev, { id: attachId, type: 'screenshot', url: '', dataUrl, name: filename, screenshotKind: kind }]);
        setTurns(prev => [...prev, { role: 'assistant' as const, content: label, attachId }]);
        (async () => {
          try {
            const urlRes = await fetch(`${API_URL}/v1/panel/upload-url`, {
              method: 'POST', headers: apiHeaders(githubLogin),
              body: JSON.stringify({ filename, contentType: 'image/png' }),
            });
            if (!urlRes.ok) return;
            const { uploadUrl, storageUrl } = await urlRes.json() as { uploadUrl: string; storageUrl: string };
            await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': 'image/png' } });
            setAttachments(prev => prev.map(a => a.id === attachId ? { ...a, url: storageUrl } : a));
          } catch { /* keep dataUrl-only */ }
        })();
      }
    } catch {
      setStatusMsg('Screenshot capture failed — try again');
      setTimeout(() => setStatusMsg(''), 2500);
    } finally {
      setIsCapturing(false);
    }
  }, [githubLogin]);

  // ── Voice ─────────────────────────────────────────────────────────────────────

  const handleMicToggle = useCallback(async () => {
    if (isRecording) {
      speechRecRef.current?.stop();
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SR) {
      const rec = new SR();
      rec.lang = 'en-US';
      rec.interimResults = false;
      rec.onresult = (event: any) => {
        const text = event.results[0][0].transcript;
        setInput(prev => prev + (prev ? ' ' : '') + text);
        setIsRecording(false);
      };
      rec.onerror = () => setIsRecording(false);
      rec.onend = () => setIsRecording(false);
      rec.start();
      speechRecRef.current = rec;
      setIsRecording(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      recorder.ondataavailable = e => audioChunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('audio', blob, 'recording.webm');
        try {
          const res = await fetch(`${API_URL}/v1/panel/transcribe`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${API_KEY}`, ...(githubLogin ? { 'x-github-login': githubLogin } : {}) },
            body: formData,
          });
          const { transcript } = await res.json() as { transcript: string };
          if (transcript) setInput(prev => prev + (prev ? ' ' : '') + transcript);
        } catch { /* ignore */ }
        setIsRecording(false);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch { /* microphone denied */ }
  }, [isRecording, githubLogin]);

  // ── Drag-and-drop ─────────────────────────────────────────────────────────────

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.relatedTarget as Node)) setIsDragOver(false);
  };
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault(); setIsDragOver(false);
    for (const file of Array.from(e.dataTransfer.files)) await uploadFile(file);
  }, [uploadFile]);

  // ── Submit ────────────────────────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || (phase !== 'chat' && phase !== 'filed')) return;
    setInput('');

    if (phase === 'filed') {
      setTurns(prev => [...prev, { role: 'user', content: text }]);
      try {
        const res = await fetch(`${API_URL}/v1/panel/comment`, {
          method: 'POST',
          headers: apiHeaders(githubLogin),
          body: JSON.stringify({ submissionId, body: text }),
        });
        if (!res.ok) throw new Error(`Comment failed: ${res.status}`);
        const { commentUrl } = await res.json() as { commentUrl: string };
        setTurns(prev => [...prev, { role: 'assistant', content: `💬 Comment posted — ${commentUrl}` }]);
      } catch (err) {
        setTurns(prev => [...prev, { role: 'assistant', content: `Failed to post comment: ${(err as Error).message}` }]);
      }
      return;
    }

    const nextTurns: Turn[] = [...turns, { role: 'user', content: text }];
    setTurns(nextTurns);
    const nextQ = questionIdx.current + 1;
    questionIdx.current = nextQ;

    if (nextQ < guidedQuestions.length) {
      setTimeout(() => setTurns(p => [...p, { role: 'assistant', content: guidedQuestions[nextQ] }]), 400);
    } else {
      setPhase('submitting');
      setStatusMsg('Analysing…');
      try {
        const transcript = nextTurns.map(t => ({ role: t.role, content: t.content }));
        const screenshotKinds: Record<string, 'page' | 'panel'> = {};
        for (const a of attachments) {
          if (a.url && a.screenshotKind) screenshotKinds[a.url] = a.screenshotKind;
        }
        const metadata: Record<string, unknown> = {
          url: window.location.href,
          title: document.title,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          viewport: { width: window.innerWidth, height: window.innerHeight },
        };
        if (Object.keys(screenshotKinds).length > 0) metadata.screenshotKinds = screenshotKinds;
        const payload: Record<string, unknown> = {
          transcript, metadata, repo: REPO,
          githubLogin: githubLogin || undefined,
        };
        const errors = consoleErrorsRef.current.slice(-20);
        if (errors.length > 0) payload.consoleErrors = errors;
        const warnings = consoleWarningsRef.current.slice(0, 30);
        if (warnings.length > 0) payload.consoleWarnings = warnings;
        if (breadcrumb.length > 0) payload.navigationBreadcrumb = breadcrumb;
        payload.componentHint = urlToComponentHint(window.location.pathname);
        if (attachments.length > 0) {
          payload.attachmentUrls = attachments.map(a => ({
            url: a.url, type: a.type, name: a.name,
          }));
        }
        const submitRes = await fetch(`${API_URL}/v1/panel/submit`, {
          method: 'POST', headers: apiHeaders(githubLogin),
          body: JSON.stringify(payload),
        });
        if (!submitRes.ok) throw new Error(`Submit failed: ${submitRes.status}`);
        const { id } = await submitRes.json() as { id: string };

        setStatusMsg('Classifying…');
        const processRes = await fetch(`${API_URL}/v1/panel/process/${id}`, {
          method: 'POST', headers: apiHeaders(githubLogin),
          body: JSON.stringify({ repo: REPO, panelRepo: PANEL_REPO }),
        });
        if (!processRes.ok) throw new Error(`Process failed: ${processRes.status}`);

        const reader = processRes.body!.getReader();
        const decoder = new TextDecoder();
        let buf = '', issueUrl = '', plainSummary = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n'); buf = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const evt = JSON.parse(line.slice(6)) as { type: string; message?: string; issueUrl?: string; plainSummary?: string };
              if (evt.message) setStatusMsg(evt.message);
              if (evt.type === 'completed') { issueUrl = evt.issueUrl ?? ''; plainSummary = evt.plainSummary ?? ''; }
              else if (evt.type === 'error') { throw new Error(evt.message ?? 'Pipeline failed'); }
            } catch { /* ignore */ }
          }
        }
        setSubmissionId(id);
        setPreview({ plain_summary: plainSummary, github_url: issueUrl });
        setStatusMsg('');
        setTurns(prev => [...prev, {
          role: 'assistant',
          content: '✅ Issue filed! You can view it on GitHub below, or type here to add a comment.',
        }]);
        setPhase('filed');
      } catch (err) { setStatusMsg((err as Error).message); setPhase('error'); }
    }
  }, [input, phase, turns, githubLogin, guidedQuestions, breadcrumb, attachments, submissionId]);

  if (!API_KEY || !API_URL) return null;

  const currentMode = MODES.find(m => m.id === activeMode)!;

  // ── Styles ────────────────────────────────────────────────────────────────────
  const s = {
    pillWrap: { position: 'fixed' as const, bottom: 20, right: 20, zIndex: 9999 },
    dropdown: {
      position: 'absolute' as const, bottom: 56, right: 0, marginBottom: 4,
      width: 176, background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '4px 0', zIndex: 10000,
    },
    dropdownItem: (active: boolean, disabled: boolean): React.CSSProperties => ({
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      width: '100%', padding: '8px 12px', fontSize: 13, border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: active ? '#f4f4f5' : 'transparent',
      fontWeight: active ? 600 : 400,
      color: disabled ? '#a1a1aa' : '#18181b', opacity: disabled ? 0.5 : 1,
      textAlign: 'left' as const,
    }),
    soonBadge: { fontSize: 10, background: '#f4f4f5', color: '#a1a1aa', borderRadius: 99, padding: '1px 6px' },
    pill: { display: 'flex', alignItems: 'center', overflow: 'hidden', borderRadius: 999, background: '#18181b', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },
    pillLeft: { display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 16, paddingRight: 12, paddingTop: 10, paddingBottom: 10, fontSize: 13, fontWeight: 500, color: '#fff', border: 'none', background: 'transparent', cursor: 'pointer' },
    pillDivider: { width: 1, height: 20, background: '#3f3f46', flexShrink: 0 as const },
    pillRight: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 12px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#fff' },
    panel: {
      position: 'fixed' as const, bottom: 80, right: 20, zIndex: 9999,
      width: 380, background: '#fff', borderRadius: 12,
      boxShadow: isDragOver ? '0 0 0 2px #60a5fa, 0 8px 32px rgba(0,0,0,0.18)' : '0 8px 32px rgba(0,0,0,0.18)',
      border: isDragOver ? '1px solid #60a5fa' : '1px solid #e4e4e7',
      display: 'flex', flexDirection: 'column' as const, fontFamily: 'inherit',
    },
    header: { padding: '12px 16px', borderBottom: '1px solid #e4e4e7', display: 'flex', justifyContent: 'space-between' as const, alignItems: 'center' },
    msgs: { flex: 1, overflowY: 'auto' as const, padding: '12px 16px', display: 'flex', flexDirection: 'column' as const, gap: 8, maxHeight: 300 },
    userMsg: { alignSelf: 'flex-end' as const, background: '#18181b', color: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 13, maxWidth: '80%' },
    asstMsg: { alignSelf: 'flex-start' as const, background: '#f4f4f5', color: '#18181b', borderRadius: 8, padding: '6px 12px', fontSize: 13, maxWidth: '80%' },
    footer: { borderTop: '1px solid #e4e4e7', padding: '8px 12px' },
    inputRow: { display: 'flex', gap: 6, alignItems: 'flex-end' as const },
    textarea: { flex: 1, resize: 'none' as const, border: '1px solid #e4e4e7', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' },
    iconBtn: (active?: boolean): React.CSSProperties => ({
      width: 32, height: 32, border: active ? 'none' : '1px solid #e4e4e7',
      borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: active ? '#ef4444' : '#fff', color: active ? '#fff' : '#71717a', flexShrink: 0,
    }),
    sendBtn: { width: 32, height: 32, background: '#18181b', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  };

  if (!API_URL || !API_KEY) return null;

  return (
    <>
      {/* ── Split button ─────────────────────────────────────────────────── */}
      <div ref={dropdownRef} style={s.pillWrap}>
        {dropdownOpen && (
          <div style={s.dropdown}>
            {MODES.map(mode => (
              <button key={mode.id} onClick={() => selectMode(mode)} disabled={mode.comingSoon} style={s.dropdownItem(activeMode === mode.id, mode.comingSoon)}>
                <span>{mode.label}</span>
                {mode.comingSoon && <span style={s.soonBadge}>Soon</span>}
                {!mode.comingSoon && activeMode === mode.id && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#71717a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </button>
            ))}
          </div>
        )}
        <div style={s.pill}>
          <button style={s.pillLeft} onClick={() => { setDropdownOpen(false); open ? (setOpen(false), reset()) : setOpen(true); }}>
            {open ? (<><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>Close</>) : (<><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>{currentMode.label}</>)}
          </button>
          <div style={s.pillDivider} />
          <button style={s.pillRight} onClick={e => { e.stopPropagation(); setDropdownOpen(o => !o); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Panel ────────────────────────────────────────────────────────── */}
      {open && (
        <div ref={panelRef} style={s.panel} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
          {/* Drag overlay */}
          {isDragOver && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(219,234,254,0.9)', borderRadius: 12, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: 13, color: '#2563eb', fontWeight: 500 }}>Drop files to attach</div>
            </div>
          )}

          <input ref={fileInputRef} type="file" multiple accept="image/*,text/*,application/pdf" style={{ display: 'none' }} onChange={async e => { for (const f of Array.from(e.target.files ?? [])) await uploadFile(f); if (fileInputRef.current) fileInputRef.current.value = ''; }} />

          <div style={s.header}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{currentMode.label}</div>
              <div style={{ fontSize: 11, color: '#71717a' }}>{githubLogin ? `@${githubLogin} · ` : ''}{REPO}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                onClick={handleNewSession}
                title="New session"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a1a1aa', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="4" x2="12" y2="20"/><line x1="4" y1="12" x2="20" y2="12"/></svg>
              </button>
              <button onClick={() => { setOpen(false); reset(); setSavedSessions([]); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#71717a' }}>✕</button>
            </div>
          </div>

          {/* Session tabs */}
          {savedSessions.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '6px 12px', borderBottom: '1px solid #f4f4f5', alignItems: 'center' }}>
              {savedSessions.map(s => {
                const label = s.turns.find(t => t.role === 'user')?.content.slice(0, 20) ?? 'Session';
                return (
                  <button key={s.id} onClick={() => handleSwitchSession(s)} title={label}
                    style={{ flexShrink: 0, border: '1px solid #e4e4e7', borderRadius: 99, padding: '2px 10px', fontSize: 11, background: 'transparent', cursor: 'pointer', color: '#71717a', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {label}
                  </button>
                );
              })}
              <div style={{ flexShrink: 0, border: 'none', borderRadius: 99, padding: '2px 10px', fontSize: 11, background: '#18181b', color: '#fff', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {turns.find(t => t.role === 'user')?.content.slice(0, 20) ?? 'Current'}
              </div>
            </div>
          )}

          <div style={s.msgs}>
            {turns.map((t, i) => (
              <div key={i} style={{ ...(t.role === 'user' ? s.userMsg : s.asstMsg), ...(t.deleted ? { opacity: 0.4 } : {}) }}>
                <span style={t.deleted ? { textDecoration: 'line-through' } : {}}>{t.content}</span>
              </div>
            ))}
            {phase === 'submitting' && <div style={s.asstMsg}>{statusMsg || 'Working…'}</div>}
            {(phase === 'filed' || phase === 'done') && preview && (
              <div style={{ background: '#f4f4f5', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                <strong>Issue filed</strong>
                <p style={{ margin: '4px 0', color: '#52525b' }}>{preview.plain_summary}</p>
                {preview.github_url && <a href={preview.github_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>View on GitHub →</a>}
              </div>
            )}
            {phase === 'error' && <div style={{ ...s.asstMsg, background: '#fef2f2', color: '#dc2626' }}>{statusMsg}</div>}
            <div ref={bottomRef} />
          </div>

          {/* Attachment strip */}
          {attachments.length > 0 && (
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '6px 12px', borderTop: '1px solid #f4f4f5' }}>
              {attachments.map(att => (
                <div key={att.id} style={{ position: 'relative', flexShrink: 0 }}>
                  {att.dataUrl ? (
                    <img src={att.dataUrl} alt={att.name} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '1px solid #e4e4e7' }} />
                  ) : (
                    <div style={{ width: 52, height: 52, background: '#f4f4f5', borderRadius: 6, border: '1px solid #e4e4e7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#71717a', textAlign: 'center', padding: 2, wordBreak: 'break-all' }}>
                      {att.name}
                    </div>
                  )}
                  <button onClick={() => {
                    setAttachments(p => p.filter(a => a.id !== att.id));
                    setTurns(p => p.map(t => t.attachId === att.id ? { ...t, deleted: true } : t));
                  }} style={{ position: 'absolute', top: -6, right: -6, width: 16, height: 16, background: '#18181b', color: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                </div>
              ))}
            </div>
          )}

          {(phase === 'chat' || phase === 'error' || phase === 'filed') && (
            <div style={s.footer}>
              <div style={s.inputRow}>
                {/* Screenshot + attach stacked vertically */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 2 }}>
                  <button onClick={() => handleScreenshot()} disabled={isCapturing} style={s.iconBtn()} title={isCapturing ? 'Capturing…' : 'Screenshot'}>
                    {isCapturing
                      ? <span style={{ fontSize: 12 }}>⏳</span>
                      : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
                  </button>
                  <button onClick={() => fileInputRef.current?.click()} style={s.iconBtn()} title="Attach file">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                  </button>
                </div>
                <textarea
                  style={s.textarea} rows={2} value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder={phase === 'filed' ? 'Add a comment to this issue…' : 'Type your message…'}
                />
                {/* Mic */}
                <button onClick={handleMicToggle} style={s.iconBtn(isRecording)} title={isRecording ? 'Stop recording' : 'Voice input'}>🎤</button>
                {/* Send */}
                <button style={s.sendBtn} onClick={handleSend} disabled={!input.trim()}>↑</button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
